import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useExternalBrowser } from "@/hooks/useExternalBrowser";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";

interface WebPricingSelectionProps {
  /** True if the user has already used their free trial (show "Subscribe" not "Start Free Trial") */
  hasHadTrial?: boolean;
}

// Trial length in days — must match Stripe checkout config
const TRIAL_DAYS = 3;

function trialEndDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + TRIAL_DAYS);
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

export const WebPricingSelection = ({ hasHadTrial = false }: WebPricingSelectionProps) => {
  const [selectedTier, setSelectedTier] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { openUrl } = useExternalBrowser();

  const price = selectedTier === "monthly" ? "$0.99" : "$9.99";
  const period = selectedTier === "monthly" ? "month" : "year";
  const yearlyEquiv = selectedTier === "yearly" ? " ($0.83/month)" : "";

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError || !session) {
        toast({
          title: "Session Error",
          description: "Please sign out and sign in again.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { tier: selectedTier },
      });

      if (error) throw error;

      if (data?.url) {
        trackEvent('subscription_started', { provider: 'stripe', tier: selectedTier, step: 'checkout_redirect' });
        await openUrl(data.url);
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 w-full">

      {/* Plan selector */}
      <div className="grid grid-cols-2 gap-3">
        {(["monthly", "yearly"] as const).map((tier) => (
          <Card
            key={tier}
            className={cn(
              "relative cursor-pointer transition-all",
              selectedTier === tier && "ring-2 ring-primary shadow-md",
            )}
            onClick={() => setSelectedTier(tier)}
          >
            {tier === "yearly" && (
              <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] px-2 bg-primary">
                Best value
              </Badge>
            )}
            <CardContent className="p-3 text-center">
              <p className="font-semibold capitalize text-sm">{tier}</p>
              <p className="text-lg font-bold mt-1">
                {tier === "monthly" ? "$0.99" : "$9.99"}
              </p>
              <p className="text-xs text-muted-foreground">
                {tier === "monthly" ? "per month" : "per year"}
              </p>
              {tier === "yearly" && (
                <p className="text-xs text-primary font-medium mt-0.5">Save 16%</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trial / pricing summary box — the key Google Play requirement */}
      {!hasHadTrial ? (
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-1">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm space-y-0.5">
              <p className="font-semibold text-foreground">
                {TRIAL_DAYS}-day free trial, then {price}/{period}{yearlyEquiv}
              </p>
              <p className="text-muted-foreground text-xs">
                Trial ends <span className="font-medium text-foreground">{trialEndDate()}</span>. No charge until then.
              </p>
              <p className="text-muted-foreground text-xs">
                After your trial, you'll be charged {price}/{period} automatically.
                Cancel anytime before the trial ends — you won't be billed.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-muted/50 border p-3">
          <p className="text-sm font-semibold">{price}/{period}{yearlyEquiv}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Subscription renews automatically. Cancel anytime.
          </p>
        </div>
      )}

      {/* Features */}
      <ul className="space-y-1.5">
        {[
          "Real-time auto-translation",
          "Voice message translation",
          "Unlimited contacts & group chats",
        ].map((f) => (
          <li key={f} className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-primary flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Button
        onClick={handlePurchase}
        disabled={loading}
        size="lg"
        className="w-full"
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {hasHadTrial
          ? `Subscribe — ${price}/${period}`
          : `Start ${TRIAL_DAYS}-Day Free Trial`}
      </Button>

      {/* Legal / auto-renewal disclosure — required by Google Play */}
      <div className="text-xs text-muted-foreground space-y-2 pt-2 border-t">
        <p>
          <strong>Subscription terms:</strong> {price}/{period} billed automatically
          {!hasHadTrial && ` after your ${TRIAL_DAYS}-day free trial`}.
          Subscription renews unless cancelled at least 24 hours before the end of the
          current billing period. Your payment method will be charged within 24 hours
          prior to the end of each period.
        </p>
        <p>
          To cancel, manage your subscription in your Google Play account settings.
        </p>
        <p>
          By subscribing you agree to our{" "}
          <a
            href="https://c16458328a2848af9115a825783212c2.lovableproject.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://c16458328a2848af9115a825783212c2.lovableproject.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary"
          >
            Terms of Service
          </a>.
        </p>
      </div>
    </div>
  );
};
